/**
 * /dashboard/posh/complaints/$id/manage — ICC/admin complaint detail +
 * actions.
 *
 * GET /posh/complaints/{id} — STRICT 2FA. A non-TOTP-enrolled caller is
 * 403'd by the backend; we detect that exact 403 and render
 * <TwoFactorRequired/> with a /2fa-setup link.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/posh/complaints/[id]/manage/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { toApiError } from "@/lib/api";
import { is2FARequiredError, useComplaint } from "@/hooks/use-posh";
import { PoshModuleShell } from "@/components/shared/posh-module-shell";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { TwoFactorRequired } from "@/components/posh/two-factor-required";
import { ComplaintDetailView } from "@/components/posh/complaint-detail-view";
import { ComplaintManageActions } from "@/components/posh/complaint-manage-actions";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/complaints/$id/manage",
)({
  component: ManageComplaintRoute,
});

function ManageComplaintRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const q = useComplaint(Number(id));

  return (
    <PoshModuleShell
      title="Complaint administration"
      description="Committee view: status, ICC assignment and documents. This area requires two-factor authentication."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/posh/complaints" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <PoshFeatureGate>
        {q.isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {q.isError && is2FARequiredError(q.error) && (
          <TwoFactorRequired surface="ICC complaint administration" />
        )}

        {q.isError && !is2FARequiredError(q.error) && (
          <Alert variant="destructive">
            <AlertTitle>Could not load this complaint</AlertTitle>
            <AlertDescription>
              {toApiError(q.error).message}
            </AlertDescription>
          </Alert>
        )}

        {q.data && (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <ComplaintDetailView data={q.data} canDownload />
            </div>
            <div>
              <ComplaintManageActions c={q.data.complaint} />
            </div>
          </div>
        )}
      </PoshFeatureGate>
    </PoshModuleShell>
  );
}
